import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import Topbar from "../components/Topbar";
import { useAuth } from "../context/AuthContext";
import { listHotels } from "../api/hotels";
import BestPriceOtaScopeTable from "./analyticsDashboard/components/BestPriceOtaScopeTable";
import DataUnavailableCard, { TECHNICAL_ERROR_MESSAGE } from "./analyticsDashboard/components/DataUnavailableCard";
import DemoAnalyticsBanner from "./analyticsDashboard/components/DemoAnalyticsBanner";
import FiltersSection from "./analyticsDashboard/components/FiltersSection";
import FiltersLoadingPlaceholder from "./analyticsDashboard/components/FiltersLoadingPlaceholder";
import GuestCountDisplay from "./analyticsDashboard/components/GuestCountDisplay";
import LoadingOverlay from "./analyticsDashboard/components/LoadingOverlay";
import ObjectSelectCard from "./analyticsDashboard/components/ObjectSelectCard";
import { DEFAULT_FILTER_OPTIONS, DEFAULT_FILTERS } from "./analyticsDashboard/constants";
import {
  downloadPriceHotelsXlsx,
  getFilters,
  getPriceHotels,
  type ApiFilterOption,
  type ApiHotelFilterOption,
  type FiltersResponse,
} from "./analyticsDashboard/api";
import {
  DEMO_ANALYTICS_GROUP_ID,
  DEMO_ANALYTICS_OBJECT_OPTION,
  DEMO_ANALYTICS_OBJECT_VALUE,
} from "./analyticsDashboard/demoObject";
import {
  LAST_PARSING_DATE_LABEL,
  isLastParsingDateInfoReady,
  useLastParsingDateInfo,
} from "./analyticsDashboard/hooks/useLastParsingDate";
import {
  isAvailableParsingDatesInfoReady,
  normalizeAvailableDate,
  useAvailableParsingDates,
} from "./analyticsDashboard/hooks/useAvailableParsingDates";
import type { FilterOption, FilterOptions, FilterState, OtaScopeHotel } from "./analyticsDashboard/types";
import { getSubscriptionState } from "../utils/subscription";
import {
  buildAnalyticsGuestsPayload,
  buildGuestSelectionOptions,
} from "../utils/guests";
import { loadHotelsDisplaySettings } from "../utils/hotelDisplaySettings";

const formatIsoDate = (date: Date) => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};
const getTodayDate = () => formatIsoDate(new Date());
const getNinetyDaysAheadDate = () => {
  const date = new Date();
  date.setDate(date.getDate() + 90);
  return formatIsoDate(date);
};
const formatDaysStayLabel = (value: number) => {
  if (value === 1) return "1 ночь";
  if (value < 5) return `${value} ночи`;
  return `${value} ночей`;
};
const mapOptions = (
  items?: Array<ApiFilterOption | string> | Record<string, string>,
): FilterOption[] => {
  if (!items) return [];
  if (!Array.isArray(items)) {
    return Object.entries(items).map(([key, value]) => ({ label: value, value: key }));
  }
  return items
    .map((item) => {
      if (typeof item === "string") return { label: item, value: item };
      if (!item) return null;
      const value = item.key ?? item.value;
      const label = item.value ?? item.key;
      if (!value || !label) return null;
      return { label, value };
    })
    .filter((item): item is FilterOption => Boolean(item));
};

const mapHotelFilterOptions = (
  items?: Array<ApiFilterOption | ApiHotelFilterOption | string> | Record<string, string>,
): FilterOption[] => {
  if (!items) return [];
  if (!Array.isArray(items)) {
    return Object.values(items).map((value) => ({ label: value, value }));
  }

  return items
    .map((item) => {
      if (typeof item === "string") return { label: item, value: item };
      if (!item) return null;
      if ("hotel_name" in item) {
        return { label: item.hotel_name, value: item.hotel_name };
      }
      const label = item.value ?? item.key;
      if (!label) return null;
      return { label, value: label };
    })
    .filter((item): item is FilterOption => Boolean(item));
};

const getDefaultPageFilters = (
  _range?: { from: string; to: string } | null,
  selectedHotel = "",
  dataCollectionDate = getTodayDate(),
): FilterState => ({
  ...DEFAULT_FILTERS,
  hotel: selectedHotel,
  date: dataCollectionDate,
  dateIntervalFrom: getTodayDate(),
  dateIntervalTo: getNinetyDaysAheadDate(),
});

const getAppliedAtLabel = () => new Date().toLocaleTimeString("ru-RU", { hour: "2-digit", minute: "2-digit" });

export default function AnalyticsPlatformsPage() {
  const { user, company_id: companyId } = useAuth();
  const subscriptionState = getSubscriptionState(user?.subscription_expires_on);
  const isSubscriptionExpired = subscriptionState.isExpired;

  const [filters, setFilters] = useState<FilterState>(getDefaultPageFilters());
  const [appliedFilters, setAppliedFilters] = useState<FilterState | null>(null);
  const [daysStay, setDaysStay] = useState<number>(1);
  const [appliedDaysStay, setAppliedDaysStay] = useState<number>(1);
  const [guests, setGuests] = useState<string>("2");
  const [appliedAt, setAppliedAt] = useState<string | null>(null);
  const [rows, setRows] = useState<OtaScopeHotel[]>([]);
  const [filterOptions, setFilterOptions] = useState<FilterOptions>(DEFAULT_FILTER_OPTIONS);
  const [checkInRange, setCheckInRange] = useState<{ from: string; to: string } | null>(null);
  const [hotelOptions, setHotelOptions] = useState<FilterOption[]>([]);
  const [hotelGroupMap, setHotelGroupMap] = useState<Record<string, number | null>>({});
  const [hotelMinStayMap, setHotelMinStayMap] = useState<Record<string, number[] | null>>({});
  const [hotelGuestsMap, setHotelGuestsMap] = useState<Record<string, number[] | null>>({});
  const [hotelGuestsChildMap, setHotelGuestsChildMap] = useState<Record<string, number[] | null>>({});
  const [selectedHotelHasCompetitors, setSelectedHotelHasCompetitors] = useState<boolean | null>(null);
  const [optionsLoading, setOptionsLoading] = useState(false);
  const [loading, setLoading] = useState(false);
  const [downloadLoading, setDownloadLoading] = useState(false);
  const [noData, setNoData] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const requestIdRef = useRef(0);
  const lastDefaultDateRef = useRef<string | null>(filters.date);
  const defaultDataCollectionDateRef = useRef(filters.date);

  const selectedGroupId = useMemo(() => {
    if (!filters.hotel) return null;
    return hotelGroupMap[filters.hotel] ?? null;
  }, [filters.hotel, hotelGroupMap]);
  const selectedHotelMinStay = useMemo(() => {
    if (!filters.hotel) return null;
    return hotelMinStayMap[filters.hotel] ?? null;
  }, [filters.hotel, hotelMinStayMap]);
  const selectedHotelGuests = useMemo(() => {
    if (!filters.hotel) return null;
    return hotelGuestsMap[filters.hotel] ?? null;
  }, [filters.hotel, hotelGuestsMap]);
  const selectedHotelGuestsChild = useMemo(() => {
    if (!filters.hotel) return null;
    return hotelGuestsChildMap[filters.hotel] ?? null;
  }, [filters.hotel, hotelGuestsChildMap]);
  const selectedHotelMinStayOptions = selectedHotelMinStay ?? [];
  const selectedHotelGuestOptions = useMemo(
    () => buildGuestSelectionOptions(selectedHotelGuests, selectedHotelGuestsChild),
    [selectedHotelGuests, selectedHotelGuestsChild],
  );
  const hasSelectedHotelMinStay = Boolean(selectedHotelMinStay?.length);
  const hasSelectedHotelGuests = Boolean(filters.hotel);
  const hasHotelSelected = Boolean(filters.hotel);
  const isDemoAnalytics = filters.hotel === DEMO_ANALYTICS_OBJECT_VALUE;
  const shouldBlockAnalyticsRequests = hasHotelSelected && !isDemoAnalytics && selectedHotelHasCompetitors !== true;
  const lastParsingDate = useLastParsingDateInfo(selectedGroupId, daysStay, !isSubscriptionExpired);
  const isLastParsingDateReady = isLastParsingDateInfoReady(
    lastParsingDate,
    selectedGroupId,
    daysStay,
    !isSubscriptionExpired,
  );
  const availableParsingDates = useAvailableParsingDates(selectedGroupId, daysStay, guests, !isSubscriptionExpired);
  const isAvailableParsingDatesReady = isAvailableParsingDatesInfoReady(
    availableParsingDates,
    selectedGroupId,
    daysStay,
    guests,
    !isSubscriptionExpired,
  );
  const defaultDataCollectionDate = lastParsingDate.date ?? getTodayDate();
  const selectedParsingDate = filters.date || defaultDataCollectionDate;
  const shouldBlockParsingDateRequests = Boolean(selectedGroupId) && (
    !isAvailableParsingDatesReady ||
    Boolean(availableParsingDates.error) ||
    availableParsingDates.dates.length === 0 ||
    !availableParsingDates.dateSet.has(selectedParsingDate)
  );
  const filtersLoading = hasHotelSelected && (optionsLoading || availableParsingDates.loading);
  const shouldWaitForDefaultDate =
    isLastParsingDateReady &&
    Boolean(lastParsingDate.date) &&
    filters.date !== lastParsingDate.date &&
    (!filters.date || filters.date === lastDefaultDateRef.current);
  const selectedHotelLabel = useMemo(
    () => hotelOptions.find((option) => option.value === filters.hotel)?.label ?? "Паритет цен",
    [hotelOptions, filters.hotel],
  );
  const tableSubtitle = useMemo(() => {
    if (!appliedFilters) return undefined;
    const parts = [`Дата сбора данных: ${appliedFilters.date || getTodayDate()}`, `Срок заселения: ${formatDaysStayLabel(appliedDaysStay)}`];
    if (appliedFilters.dateIntervalFrom || appliedFilters.dateIntervalTo) {
      parts.unshift(`Дата заселения: ${appliedFilters.dateIntervalFrom || "—"} - ${appliedFilters.dateIntervalTo || "—"}`);
    }
    return parts.join(" · ");
  }, [appliedDaysStay, appliedFilters]);

  useEffect(() => {
    if (!selectedHotelMinStay?.length) {
      setDaysStay(1);
      return;
    }
    if (!selectedHotelMinStay.includes(daysStay)) {
      setDaysStay(selectedHotelMinStay[0]);
    }
  }, [daysStay, selectedHotelMinStay]);

  useEffect(() => {
    if (!selectedHotelGuestOptions.some((option) => option.value === guests)) {
      setGuests(selectedHotelGuestOptions[0]?.value ?? "2");
    }
  }, [guests, selectedHotelGuestOptions]);

  useEffect(() => {
    const lastDate = lastParsingDate.date;
    if (!lastDate) return;
    defaultDataCollectionDateRef.current = lastDate;
    const currentDefaultDate = lastDefaultDateRef.current;
    setFilters((prev) => {
      if (!prev.hotel) return prev;
      const shouldApplyDefault = !prev.date || prev.date === currentDefaultDate;
      if (!shouldApplyDefault || prev.date === lastDate) return prev;
      return { ...prev, date: lastDate };
    });
    lastDefaultDateRef.current = lastDate;
  }, [lastParsingDate.date]);

  useEffect(() => {
    if (!selectedGroupId || !isAvailableParsingDatesReady || availableParsingDates.error) return;
    setFilters((prev) => {
      if (!prev.hotel) return prev;
      const nextDate = normalizeAvailableDate(prev.date, defaultDataCollectionDate, availableParsingDates.dates);
      if (prev.date === nextDate) return prev;
      return { ...prev, date: nextDate };
    });
  }, [
    availableParsingDates.dates,
    availableParsingDates.datesKey,
    availableParsingDates.error,
    defaultDataCollectionDate,
    isAvailableParsingDatesReady,
    selectedGroupId,
  ]);

  useEffect(() => {
    if (!companyId) return;
    let isMounted = true;
    setOptionsLoading(true);

    listHotels(companyId, { page: 1, per_page: 200 })
      .then(async (response) => {
        if (!isMounted) return;
        const nextHotelOptions = response.items.map((item) => ({ label: item.name, value: String(item.id) }));
        if (response.items.length === 0) {
          const nextFilters = getDefaultPageFilters(undefined, DEMO_ANALYTICS_OBJECT_VALUE);
          lastDefaultDateRef.current = nextFilters.date;
          defaultDataCollectionDateRef.current = nextFilters.date;
          setHotelOptions([DEMO_ANALYTICS_OBJECT_OPTION]);
          setHotelGroupMap({ [DEMO_ANALYTICS_OBJECT_VALUE]: DEMO_ANALYTICS_GROUP_ID });
          setHotelMinStayMap({ [DEMO_ANALYTICS_OBJECT_VALUE]: null });
          setHotelGuestsMap({ [DEMO_ANALYTICS_OBJECT_VALUE]: null });
          setHotelGuestsChildMap({ [DEMO_ANALYTICS_OBJECT_VALUE]: null });
          setNoData(false);
          setError(null);
          setRows([]);
          setAppliedFilters(null);
          setAppliedAt(null);
          setFilters(nextFilters);
          return;
        }
        const displaySettingsMap = await loadHotelsDisplaySettings(response.items);
        if (!isMounted) return;
        const nextHotelGroupMap = response.items.reduce<Record<string, number | null>>((acc, item) => {
          acc[String(item.id)] = item.group_id ?? null;
          return acc;
        }, {});
        const nextHotelMinStayMap = response.items.reduce<Record<string, number[] | null>>((acc, item) => {
          acc[String(item.id)] = displaySettingsMap[String(item.id)]?.min_stay ?? item.min_stay ?? null;
          return acc;
        }, {});
        const nextHotelGuestsMap = response.items.reduce<Record<string, number[] | null>>((acc, item) => {
          acc[String(item.id)] = displaySettingsMap[String(item.id)]?.guests ?? item.guests ?? null;
          return acc;
        }, {});
        const nextHotelGuestsChildMap = response.items.reduce<Record<string, number[] | null>>((acc, item) => {
          acc[String(item.id)] = displaySettingsMap[String(item.id)]?.guests_child ?? item.guests_child ?? null;
          return acc;
        }, {});
        setHotelOptions(nextHotelOptions);
        setHotelGroupMap(nextHotelGroupMap);
        setHotelMinStayMap(nextHotelMinStayMap);
        setHotelGuestsMap(nextHotelGuestsMap);
        setHotelGuestsChildMap(nextHotelGuestsChildMap);
        setFilters((prev) => {
          if (response.items.length === 1) {
            const nextFilters = getDefaultPageFilters(undefined, String(response.items[0].id));
            lastDefaultDateRef.current = nextFilters.date;
            defaultDataCollectionDateRef.current = nextFilters.date;
            return nextFilters;
          }
          if (nextHotelOptions.some((item) => item.value === prev.hotel)) {
            return prev;
          }
          const nextFilters = getDefaultPageFilters();
          lastDefaultDateRef.current = nextFilters.date;
          defaultDataCollectionDateRef.current = nextFilters.date;
          return nextFilters;
        });
      })
      .catch(() => {
        if (!isMounted) return;
        setError("Не удалось загрузить список объектов");
      })
      .finally(() => {
        if (isMounted) setOptionsLoading(false);
      });

    return () => {
      isMounted = false;
    };
  }, [companyId]);

  const loadTable = useCallback(async (groupId: number, currentFilters: FilterState) => {
    if (isSubscriptionExpired) {
      setLoading(false);
      setNoData(false);
      setError(null);
      setRows([]);
      return;
    }
    const requestId = requestIdRef.current + 1;
    requestIdRef.current = requestId;
    setLoading(true);
    setNoData(false);
    setError(null);
    const effectiveDaysStay =
      selectedHotelMinStay && selectedHotelMinStay.length > 0
        ? (selectedHotelMinStay.includes(daysStay) ? daysStay : selectedHotelMinStay[0])
        : daysStay;
    const requestDate = currentFilters.date || defaultDataCollectionDate;

    if (!availableParsingDates.dateSet.has(requestDate)) {
      setLoading(false);
      setNoData(false);
      setRows([]);
      return;
    }

    try {
      const response = await getPriceHotels({
        group_id: groupId,
        date: requestDate,
        date_from: currentFilters.dateIntervalFrom || undefined,
        date_to: currentFilters.dateIntervalTo || undefined,
        category: currentFilters.category.length ? currentFilters.category : undefined,
        ota: currentFilters.ota.length ? currentFilters.ota : undefined,
        board_type: currentFilters.meals.length ? currentFilters.meals : undefined,
        refund_policy: currentFilters.refund.length ? currentFilters.refund : undefined,
        payment_type: currentFilters.payment.length ? currentFilters.payment : undefined,
        hotel: currentFilters.competitor.length ? currentFilters.competitor : undefined,
        is_avg: false,
        ...(effectiveDaysStay > 1 ? { days_stay: effectiveDaysStay } : {}),
        ...buildAnalyticsGuestsPayload(guests),
      });

      if (requestId !== requestIdRef.current) return;
      setRows(response);
      setNoData(response.length === 0);
      setAppliedFilters(currentFilters);
      setAppliedDaysStay(effectiveDaysStay);
      setAppliedAt(getAppliedAtLabel());
    } catch (err: any) {
      if (requestId !== requestIdRef.current) return;
      setNoData(false);
      setError(err?.response?.data?.detail || "Не удалось загрузить данные по платформам");
      setRows([]);
    } finally {
      if (requestId === requestIdRef.current) {
        setLoading(false);
      }
    }
  }, [availableParsingDates.dateSet, daysStay, defaultDataCollectionDate, guests, isSubscriptionExpired, selectedHotelMinStay]);

  useEffect(() => {
    if (!selectedGroupId) {
      setFilterOptions(DEFAULT_FILTER_OPTIONS);
      setCheckInRange(null);
      setSelectedHotelHasCompetitors(null);
      setRows([]);
      setAppliedFilters(null);
      setAppliedAt(null);
      setNoData(false);
      setError(null);
      return;
    }

    let isMounted = true;
    setOptionsLoading(true);
    setSelectedHotelHasCompetitors(isDemoAnalytics ? true : null);
    setError(null);

    getFilters(selectedGroupId)
      .then((response: FiltersResponse) => {
        if (!isMounted) return;
        const nextRange = response.check_in_dates ?? null;
        const nextFilters = getDefaultPageFilters(
          nextRange,
          filters.hotel,
          defaultDataCollectionDateRef.current,
        );
        lastDefaultDateRef.current = nextFilters.date;
        const hotelFilterOptions = mapHotelFilterOptions(response.hotel);
        const hasCompetitors = Array.isArray(response.hotel)
          ? response.hotel.some((item) => {
              if (typeof item === "string") return true;
              if (!item) return false;
              if ("hotel_name" in item) {
                return !(item.is_base === true || item.is_base === 1 || item.is_base === "1");
              }
              return true;
            })
          : hotelFilterOptions.length > 0;

        setSelectedHotelHasCompetitors(isDemoAnalytics || hasCompetitors);
        setFilterOptions({
          dataSource: [],
          room: [],
          category: mapOptions(response.category),
          ota: mapOptions(response.ota),
          meals: mapOptions(response.board_type),
          refund: mapOptions(response.refund_policy),
          payment: mapOptions(response.payment_type),
          competitors: hotelFilterOptions,
        });
        setCheckInRange(nextRange);
        setFilters(nextFilters);
      })
      .catch(() => {
        if (!isMounted) return;
        setError("Не удалось загрузить фильтры");
        setFilterOptions(DEFAULT_FILTER_OPTIONS);
        setCheckInRange(null);
      })
      .finally(() => {
        if (isMounted) setOptionsLoading(false);
      });

    return () => {
      isMounted = false;
    };
  }, [filters.hotel, isDemoAnalytics, selectedGroupId]);

  useEffect(() => {
    if (
      isSubscriptionExpired ||
      !selectedGroupId ||
      shouldBlockAnalyticsRequests ||
      shouldBlockParsingDateRequests ||
      !isLastParsingDateReady ||
      !isAvailableParsingDatesReady ||
      shouldWaitForDefaultDate
    ) return;
    void loadTable(selectedGroupId, filters);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
      selectedGroupId,
      daysStay,
      guests,
      isSubscriptionExpired,
      shouldBlockAnalyticsRequests,
      shouldBlockParsingDateRequests,
      isLastParsingDateReady,
      isAvailableParsingDatesReady,
      shouldWaitForDefaultDate,
      loadTable,
    ]);

  const handleApply = () => {
    if (!selectedGroupId || isSubscriptionExpired || shouldBlockAnalyticsRequests || shouldBlockParsingDateRequests || !isLastParsingDateReady || !isAvailableParsingDatesReady) return;
    void loadTable(selectedGroupId, filters);
  };

  const handleReset = () => {
    if (!selectedGroupId || isSubscriptionExpired || shouldBlockAnalyticsRequests || !isLastParsingDateReady || !isAvailableParsingDatesReady || availableParsingDates.error) return;
    const resetDate = normalizeAvailableDate(defaultDataCollectionDate, defaultDataCollectionDate, availableParsingDates.dates);
    const nextFilters = getDefaultPageFilters(checkInRange, filters.hotel, resetDate);
    lastDefaultDateRef.current = nextFilters.date;
    defaultDataCollectionDateRef.current = nextFilters.date;
    setFilters(nextFilters);
    void loadTable(selectedGroupId, nextFilters);
  };

  const handleDownloadXlsx = async () => {
    if (!selectedGroupId || !appliedFilters || downloadLoading || isSubscriptionExpired || shouldBlockAnalyticsRequests) return;
    if (shouldBlockParsingDateRequests) return;

    setDownloadLoading(true);

    try {
      const { blob, filename } = await downloadPriceHotelsXlsx({
        group_id: selectedGroupId,
        date: appliedFilters.date || undefined,
        date_from: appliedFilters.dateIntervalFrom || undefined,
        date_to: appliedFilters.dateIntervalTo || undefined,
        category: appliedFilters.category.length ? appliedFilters.category : undefined,
        ota: appliedFilters.ota.length ? appliedFilters.ota : undefined,
        board_type: appliedFilters.meals.length ? appliedFilters.meals : undefined,
        refund_policy: appliedFilters.refund.length ? appliedFilters.refund : undefined,
        payment_type: appliedFilters.payment.length ? appliedFilters.payment : undefined,
        hotel: appliedFilters.competitor.length ? appliedFilters.competitor : undefined,
        is_avg: false,
        ...(appliedDaysStay > 1 ? { days_stay: appliedDaysStay } : {}),
        ...buildAnalyticsGuestsPayload(guests),
      });
      const downloadUrl = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = downloadUrl;
      link.download = filename || `price-hotels-${filters.hotel || "report"}.xlsx`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(downloadUrl);
    } catch (downloadError) {
      console.error("Не удалось скачать отчет 'Паритет цен''", downloadError);
    } finally {
      setDownloadLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 via-slate-50 to-slate-100 text-slate-900">
      <Topbar />

      <main className="px-6 py-8">
        <div className="mx-auto flex max-w-[1600px] flex-col gap-6">
          <section className="relative overflow-hidden rounded-3xl border border-white/70 bg-white/90 p-6 shadow-[0_18px_40px_rgba(15,23,42,0.08)] backdrop-blur">
            <div className="pointer-events-none absolute inset-y-0 right-0 w-56 translate-x-10 rounded-full bg-gradient-to-b from-indigo-100 via-violet-100 to-white blur-3xl opacity-80" />
            <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
              <div>
                <h1 className="mt-2 text-3xl font-bold text-slate-900">Паритет цен</h1>
                <p className="mt-2 max-w-4xl text-sm text-slate-500">
                  Сравнение цен по OTA для выбранного объекта на диапазоне дат заселения.
                </p>
              </div>
              <div className="flex flex-col gap-3 sm:flex-row sm:items-start">
                <div className="flex flex-col gap-3 self-start">
                  {hasHotelSelected && hasSelectedHotelMinStay && (
                    <div className="flex items-center gap-3 self-start sm:self-auto">
                    <span className="text-xs font-semibold uppercase tracking-[0.08em] text-indigo-500">Срок заселения</span>
                    <div className="inline-flex rounded-full border border-indigo-200 bg-white p-1 shadow-sm">
                      {selectedHotelMinStayOptions.map((value) => {
                        const isActive = daysStay === value;
                        return (
                          <button
                            key={value}
                            type="button"
                            onClick={() => {
                              setDaysStay(value);
                              setAppliedAt(getAppliedAtLabel());
                            }}
                            className={[
                              "rounded-full px-3 py-1 text-xs font-semibold transition",
                              isActive ? "bg-indigo-600 text-white" : "text-slate-600 hover:bg-indigo-50",
                            ].join(" ")}
                            aria-pressed={isActive}
                          >
                            {formatDaysStayLabel(value)}
                          </button>
                        );
                      })}
                    </div>
                    </div>
                  )}
                  {hasHotelSelected && hasSelectedHotelGuests && (
                    <GuestCountDisplay value={guests} options={selectedHotelGuestOptions} onChange={setGuests} />
                  )}
                </div>
                <div className="rounded-2xl border border-indigo-100 bg-indigo-50/70 px-5 py-4 text-sm text-indigo-700">
                  {lastParsingDate.display ? `${LAST_PARSING_DATE_LABEL}: ${lastParsingDate.display}` : appliedAt
                    ? `Фильтры обновлены в ${appliedAt}`
                    : "Выберите объект размещения — фильтры и данные загрузятся автоматически"}
                </div>
              </div>
            </div>
          </section>

          {error && <DataUnavailableCard message={TECHNICAL_ERROR_MESSAGE} variant="error" />}

          {isDemoAnalytics && <DemoAnalyticsBanner />}

          <ObjectSelectCard
            value={filters.hotel}
            options={hotelOptions}
            onChange={(value) => {
              setNoData(false);
              setSelectedHotelHasCompetitors(value === DEMO_ANALYTICS_OBJECT_VALUE ? true : null);
              setRows([]);
              setAppliedFilters(null);
              setAppliedAt(null);
              setFilters((prev) => ({ ...prev, hotel: value }));
            }}
            loading={optionsLoading && hotelOptions.length === 0}
          />

          {hasHotelSelected && filtersLoading ? (
            <FiltersLoadingPlaceholder label="Загружаем фильтры и доступные даты..." />
          ) : hasHotelSelected && (
            <FiltersSection
              filters={filters}
              options={filterOptions}
              onChange={setFilters}
              onApply={handleApply}
              onReset={handleReset}
              checkInRange={checkInRange}
              loading={loading || optionsLoading || !isLastParsingDateReady || shouldBlockAnalyticsRequests}
              applyDisabled={shouldBlockParsingDateRequests}
              parsingDateAvailability={{
                dates: availableParsingDates.dates,
                loading: availableParsingDates.loading,
                error: availableParsingDates.error,
                restrictDate: true,
              }}
              showCompetitors
              showDateInterval
              dateLabel="Дата сбора данных"
              dateIntervalLabel="Дата заселения"
            />
          )}

          {!hasHotelSelected && (
            <div className="rounded-3xl border border-dashed border-slate-300 bg-white/90 px-6 py-10 text-center text-slate-500">
              Выберите объект размещения, чтобы увидеть цены по OTA платформам.
            </div>
          )}

          {hasHotelSelected && !isSubscriptionExpired && !error && !loading && selectedHotelHasCompetitors === false && (
            <DataUnavailableCard
              message={
                <>
                  Для начала работы добавьте конкурентов в{" "}
                  <Link className="text-indigo-600 underline decoration-indigo-300 underline-offset-4 hover:text-indigo-700" to={`/my/objects/${filters.hotel}`}>
                    объект размещения
                  </Link>
                </>
              }
            />
          )}

          {hasHotelSelected && !isSubscriptionExpired && !error && !loading && selectedHotelHasCompetitors === true && appliedFilters && noData && <DataUnavailableCard />}

          {hasHotelSelected && !isSubscriptionExpired && selectedHotelHasCompetitors === true && (
            <section className="relative">
              <LoadingOverlay visible={loading} label="Загружаем данные по OTA..." />
              {!error && !noData && rows.length > 0 && (
                <BestPriceOtaScopeTable
                  title={`Сравнение OTA по мин. цене · ${selectedHotelLabel}`}
                  subtitle={tableSubtitle}
                  data={rows}
                  onDownload={handleDownloadXlsx}
                  downloadLoading={downloadLoading}
                />
              )}
            </section>
          )}
        </div>
      </main>
    </div>
  );
}
